import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import CancelPlotPrint from './CancelPlotPrint'

// Pakistani Rupee formatter
const formatPakistaniCurrency = amount => {
  if (amount === null || amount === undefined) return 'PKR 0'
  return `PKR ${Number(amount).toLocaleString('en-PK')}`
}

// Simplified date formatter
const formatDate = dateString => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-PK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const CancelPlotDetails = ({
  plotDetails,
  onProcessRefund,
  themeColor = 'teal',
  activeSociety
}) => {
  const [activeTab, setActiveTab] = useState('summary')

  if (!plotDetails) {
    return (
      <div className='bg-white dark:bg-slate-800 rounded-xl shadow-sm p-8 flex items-center justify-center h-80 border border-slate-200 dark:border-slate-700'>
        <div className='flex flex-col items-center space-y-4'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600'></div>
          <p className='text-slate-500 font-medium'>Loading plot details...</p>
        </div>
      </div>
    )
  }

  const {
    plot_id,
    original_booking,
    total_refund_amount = 0,
    refunded_amount = 0,
    refund_type = 'full',
    dealer_commission_deducted = 0,
    society_deduction_percentage = 0,
    society_deduction_amount = 0,
    remaining_balance = 0,
    refund_completed = false,
    cancellation_reason = 'Not specified',
    createdAt
  } = plotDetails

  // Get the original amount safely
  const originalAmount =
    original_booking?.total_amount ||
    (plot_id?.sale_history?.length > 0
      ? plot_id.sale_history[plot_id.sale_history.length - 1].sale_amount
      : plot_id?.price || 0)

  // Calculate Net Refund Amount
  const netRefundAmount =
    total_refund_amount -
    (dealer_commission_deducted || 0) -
    (society_deduction_amount || 0)

  // Calculate refund progress percentage based on NET REFUND
  const refundProgressPercentage =
    netRefundAmount > 0
      ? Math.round((refunded_amount / netRefundAmount) * 100)
      : refund_completed
        ? 100
        : 0

  const tabs = [
    { id: 'summary', label: 'Summary' },
    { id: 'financials', label: 'Financials' }
  ]

  return (
    <div className='bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-sm border border-slate-200 dark:border-slate-700'>
      {/* Header */}
      <div className='p-6 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800'>
        <div>
          <h2 className='text-xl font-bold text-slate-800 dark:text-white'>
            Cancellation Details
          </h2>
          <p className='text-sm text-slate-500'>
            {plot_id?.plot_number} • {plot_id?.block}
          </p>
        </div>

        <div className='flex items-center space-x-3'>
          <CancelPlotPrint plotDetails={plotDetails} activeSociety={activeSociety} />

          {!refund_completed && (
            <button
              onClick={onProcessRefund}
              className='bg-teal-600 hover:bg-teal-700 text-white text-sm px-4 py-2 rounded-lg shadow-sm transition duration-200 font-medium flex items-center space-x-2'
            >
              <span>Process Refund</span>
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M14 5l7 7m0 0l-7 7m7-7H3'
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className='flex border-b border-slate-200 dark:border-slate-700'>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 text-sm font-medium transition-colors duration-200 border-b-2 ${activeTab === tab.id
                ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className='p-6 min-h-[400px]'>
        <AnimatePresence mode='wait'>
          {activeTab === 'summary' && (
            <motion.div
              key='summary'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className='space-y-6'
            >
              {/* Status Banner */}
              <div className={`p-4 rounded-lg flex items-center justify-between ${refund_completed ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                <div className='flex items-center space-x-3'>
                  {refund_completed ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  )}
                  <div>
                    <h3 className='font-bold'>{refund_completed ? 'Refund Completed' : 'Refund In Progress'}</h3>
                    <p className='text-sm opacity-90'>{refund_completed ? 'All payments have been settled.' : 'Refund installments are currently active.'}</p>
                  </div>
                </div>
                <div className='text-right'>
                  <div className='text-xs uppercase tracking-wide opacity-70'>Cancelled On</div>
                  <div className='font-semibold'>{formatDate(createdAt)}</div>
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div className='bg-slate-50 dark:bg-slate-700/30 p-5 rounded-lg border border-slate-100 dark:border-slate-700'>
                  <h3 className='text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4 border-b border-slate-200 pb-2'>Plot Details</h3>
                  <div className='space-y-3 text-sm'>
                    <div className='flex justify-between'>
                      <span className='text-slate-500'>Plot Number</span>
                      <span className='font-semibold text-slate-700 dark:text-slate-200'>{plot_id?.plot_number || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-slate-500'>Block</span>
                      <span className='font-semibold text-slate-700 dark:text-slate-200'>{plot_id?.block || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-slate-500'>Size</span>
                      <span className='font-semibold text-slate-700 dark:text-slate-200'>{plot_id?.marla ? `${plot_id.marla} Marla` : 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className='bg-slate-50 dark:bg-slate-700/30 p-5 rounded-lg border border-slate-100 dark:border-slate-700'>
                  <h3 className='text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4 border-b border-slate-200 pb-2'>Cancellation Info</h3>
                  <div className='space-y-3 text-sm'>
                    <div className='flex justify-between'>
                      <span className='text-slate-500'>Refund Type</span>
                      <span className='font-semibold text-slate-700 dark:text-slate-200 capitalize'>{refund_type}</span>
                    </div>
                    <div>
                      <span className='text-slate-500 block mb-1'>Reason</span>
                      <p className='text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 p-2 rounded border border-slate-200 dark:border-slate-600 text-xs leading-relaxed'>
                        {cancellation_reason}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'financials' && (
            <motion.div
              key='financials'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className='space-y-6'
            >
              {/* Financial Summary Card */}
              <div className='bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden'>
                <div className='bg-slate-50 dark:bg-slate-700/50 p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center'>
                  <h3 className='font-bold text-slate-700 dark:text-white'>Net Refund Calculation</h3>
                  <span className='text-xs text-slate-500'>Breakdown of deductions</span>
                </div>

                <div className='p-0'>
                  <table className='w-full text-sm text-left'>
                    <tbody className='divide-y divide-slate-100 dark:divide-slate-700'>
                      <tr>
                        <td className='p-4 text-slate-500'>Total Paid Amount (Gross)</td>
                        <td className='p-4 text-right font-medium text-slate-700 dark:text-slate-200'>{formatPakistaniCurrency(total_refund_amount)}</td>
                      </tr>
                      {dealer_commission_deducted > 0 && (
                        <tr>
                          <td className='p-4 text-slate-500'>Dealer Commission</td>
                          <td className='p-4 text-right font-medium text-red-500'>- {formatPakistaniCurrency(dealer_commission_deducted)}</td>
                        </tr>
                      )}
                      <tr>
                        <td className='p-4 text-slate-500'>Society Deduction ({society_deduction_percentage}%)</td>
                        <td className='p-4 text-right font-medium text-red-500'>- {formatPakistaniCurrency(society_deduction_amount)}</td>
                      </tr>
                      <tr className='bg-slate-50 dark:bg-slate-700/30'>
                        <td className='p-4 font-bold text-slate-800 dark:text-white'>Net Refundable Amount</td>
                        <td className='p-4 text-right font-bold text-emerald-600 dark:text-emerald-400 text-lg'>{formatPakistaniCurrency(netRefundAmount)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Progress Section */}
              <div className='bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5'>
                <div className='flex justify-between items-end mb-2'>
                  <h4 className='font-medium text-slate-700 dark:text-slate-200'>Refund Progress</h4>
                  <span className='font-bold text-teal-600'>{refundProgressPercentage}% Paid</span>
                </div>
                <div className='w-full bg-slate-100 dark:bg-slate-700 rounded-full h-3 mb-4'>
                  <div
                    className='bg-teal-500 h-3 rounded-full transition-all duration-500'
                    style={{ width: `${refundProgressPercentage}%` }}
                  ></div>
                </div>
                <div className='flex justify-between text-sm'>
                  <div className='flex flex-col'>
                    <span className='text-slate-500 text-xs'>Paid so far</span>
                    <span className='font-semibold text-emerald-600'>{formatPakistaniCurrency(refunded_amount)}</span>
                  </div>
                  <div className='flex flex-col text-right'>
                    <span className='text-slate-500 text-xs'>Remaining Balance</span>
                    <span className='font-semibold text-slate-700 dark:text-white'>{formatPakistaniCurrency(remaining_balance)}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default CancelPlotDetails
